import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, ClientOptionsDispatchers, ClientOptionsSelectors, ClientService, KunderAdrService } from '@store';
import { CaseName, Client, Faktura, Fakturalinjer, KunderAdr, PatchObject, Settings } from '@store/models';
import { CaseInvoicing } from 'app/invoices/models/CaseInvoicing';
import { Observable, of } from 'rxjs';
import { map, take, tap, withLatestFrom } from 'rxjs/operators';
import { FakturaService } from '../services/faktura.service';
import { AlertService } from '../../core/alerts/alert.service';

@Component({
    selector: 'app-invoice',
    template: `<app-invoice-ui
        [faktura]="faktura$ | async"
        [officeSettings]="officeSettings$ | async"
        [kunderAdr]="kunderAdr$ | async"
        [client]="client$ | async"
        [createCases]="createCases$ | async"
        [caseNames]="caseNames$ | async"
        [interestRate]="interestRate$ | async"
        (updateFaktura)="updateFaktura($event)"
        (godkendFaktura)="godkendFaktura($event)"
        (sendFaktura)="sendFaktura($event)"
        (deleteFaktura)="deleteFaktura($event)"
        (deleteFakturalinje)="deleteFakturalinje($event)"
        (saveFakturalinje)="saveFakturalinje($event)"
    ></app-invoice-ui>`,
    standalone: false
})
export class InvoiceContainerComponent implements OnInit {
    @Input() fnr: number;
    @Input() kid: number;
    faktura$: Observable<Faktura>;
    officeSettings$: Observable<Settings>;
    kunderAdr$: Observable<KunderAdr[]>;
    client$: Observable<Client>;
    createCases$: Observable<CaseInvoicing[]>;
    caseNames$: Observable<CaseName[]>;
    interestRate$: Observable<number>;

    constructor(
        private fakturaService: FakturaService,
        private authService: AuthService,
        private kunderAdrService: KunderAdrService,
        private clientService: ClientService,
        private activeModal: NgbActiveModal,
        private clientOptionsDispatch: ClientOptionsDispatchers,
        private clientOptionsSelector: ClientOptionsSelectors,
        private alert: AlertService
    ) { }

    ngOnInit(): void {
        this.clientOptionsDispatch.loadClientOptions();
        this.officeSettings$ = this.authService.getOfficeSettings();
        this.getFaktura();
        this.getClientInfo();
    }

    getFaktura() {
        if (this.fnr) {
            this.faktura$ = this.fakturaService.fakturaByFnr(this.fnr).pipe(
                map(faktura => this.sortFakturalinjer(faktura))
            );
        }
    }

    getClientInfo() {
        if (this.kid) {
            this.client$ = this.clientService.getKunde(this.kid);
            this.interestRate$ = this.client$.pipe(
                withLatestFrom(this.clientOptionsSelector.financeChargeTerms),
                map(([client, chargeTerms]) => chargeTerms.find(term => term.code === client.navRentebetingelser)),
                map((term) => term?.interestRate)
            );
            this.kunderAdr$ = this.kunderAdrService.getKunderAdr(this.kid);
            this.createCases$ = this.fakturaService.casesWithTotals(this.kid);
            this.caseNames$ = this.fakturaService.caseNames(this.kid).pipe(
                map(caseNames => ([{pid: 0, beskrivelse: 'Aconto'}, ...caseNames]))
            );
        }
    }

    updateFaktura(data: {fnr: number, partialFaktura: Partial<Faktura>}) {
        const patchValues: PatchObject[] = [];
        Object.keys(data.partialFaktura).forEach(key => {
            patchValues.push({value: data.partialFaktura[key], path: '/' + key, op: 'replace'});
        });
        this.fakturaService.updateFaktura(data.fnr, patchValues).pipe(
            take(1),
            tap(faktura => this.faktura$ = of(this.sortFakturalinjer(faktura))),
            tap(() => this.createCases$ = this.fakturaService.casesWithTotals(this.kid))
        ).subscribe();
    }

    godkendFaktura(fnr: number) {
        this.fakturaService.godkendFaktura(fnr).pipe(
            take(1),
            tap(faktura => this.faktura$ = of(this.sortFakturalinjer(faktura)))
        ).subscribe();
    }


    deleteFaktura(fnr: number) {
        this.fakturaService.deleteFaktura(fnr).pipe(
            take(1),
            tap(success => {
                if (success) { this.activeModal.close(); }
            })
        ).subscribe();
    }

    deleteFakturalinje(flid: number) {
        this.fakturaService.deleteFakturalinje(flid).pipe(
            take(1),
            tap(faktura => this.faktura$ = of(this.sortFakturalinjer(faktura))),
            tap(() => this.createCases$ = this.fakturaService.casesWithTotals(this.kid))
        ).subscribe();
    }

    saveFakturalinje(fakturalinje: Fakturalinjer) {
        this.fakturaService.saveFakturalinje(fakturalinje).pipe(
            take(1),
            tap(faktura => this.faktura$ = of(this.sortFakturalinjer(faktura))),
            tap(() => this.createCases$ = this.fakturaService.casesWithTotals(this.kid))
        ).subscribe();
    }

    sendFaktura(sendData: {faktura: Faktura; blob: Blob, fakturaEmail: string}) {
        this.fakturaService.emailFakutra(sendData.faktura, sendData.blob).pipe(
            take(1),
            tap(faktura => this.faktura$ = of(this.sortFakturalinjer(faktura)))
        ).subscribe(
            () => this.alert.createAlert(`Faktura er sendt til kunden på ${sendData.fakturaEmail}`, 'success')
        );
    }

    private sortFakturalinjer(faktura: Faktura): Faktura {
        return {
            ...faktura,
            fakturalinjer: faktura.fakturalinjer.sort((a, b) => a.flid - b.flid)
        };
    }

}
